/**
 * THE FILE — state marks. How a professional position appears on screen, and
 * how it never appears.
 *
 * A state is words plus a 7px square. It is not a pill, not a chip, not a
 * coloured background and not a percentage. The reason is plain: a coloured
 * pill reads as a verdict at a glance, and half the states this product
 * carries are not verdicts — "waiting for evidence" is the normal condition of
 * careful work, and a green or red badge would state something stronger than
 * the record does.
 */
import { clsx } from "clsx";
import type { ReactNode } from "react";
import { MARKER_FILL, type MarkerTone } from "./tone.js";

export interface MarkerProps {
  /** Defaults to `progress` — the tone that claims the least. */
  tone?: MarkerTone | undefined;
  className?: string | undefined;
}

/**
 * A 7px state square. `aria-hidden` because it says nothing a screen reader
 * could use: the state is in the words beside it, and a reader that announced
 * "amber square" would be reading the decoration instead of the position.
 */
export function Marker({ tone = "progress", className }: MarkerProps) {
  return (
    <span
      aria-hidden="true"
      className={clsx("inline-block h-[7px] w-[7px] shrink-0 rounded-[1px]", MARKER_FILL[tone], className)}
    />
  );
}

export interface StateTextProps {
  tone?: MarkerTone | undefined;
  children: ReactNode;
  className?: string | undefined;
}

/** Marker plus words, inline. One vocabulary for every state on every screen. */
export function StateText({ tone = "progress", children, className }: StateTextProps) {
  return (
    <span className={clsx("inline-flex items-center gap-2 text-ink", className)}>
      <Marker tone={tone} />
      <span>{children}</span>
    </span>
  );
}

export interface NoticeProps {
  tone?: MarkerTone | undefined;
  children: ReactNode;
  className?: string | undefined;
}

/**
 * A designed line for an empty, refused or failed state — never a box.
 *
 * A bordered alert panel is the reflex here, and it is wrong for this product:
 * boxing the sentence makes the failure the loudest object on the page even
 * when it is a quiet statement of fact ("no records yet"). The marker carries
 * the tone; the sentence carries everything else. `role="alert"` only on
 * conflict, because that is the only tone worth interrupting a reader for.
 */
export function Notice({ tone = "conflict", children, className }: NoticeProps) {
  const text = tone === "conflict" ? "text-con-text" : tone === "attention" ? "text-att-text" : "text-ink";
  return (
    <p className={clsx("flex items-start gap-2 text-sm", text, className)} role={tone === "conflict" ? "alert" : undefined}>
      <Marker tone={tone} className="mt-[7px]" />
      <span>{children}</span>
    </p>
  );
}

export interface ReadingProps {
  children?: ReactNode;
  className?: string | undefined;
}

/**
 * Loading, stated. A caption, not a spinner.
 *
 * Nothing moves, and nothing claims a stage, a percentage or an elapsed time.
 * A rotating thing implies work is proceeding at a rate somebody is measuring;
 * on this path the outcome commits in one transaction and there is nothing in
 * between to report. Saying so in a line of text is both honest and calmer.
 */
export function Reading({ children = "Loading…", className }: ReadingProps) {
  return (
    <p className={clsx("text-[13px] text-stone", className)} role="status">
      {children}
    </p>
  );
}
